import React, { Fragment, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
	faCheck,
	faChevronLeft,
	faChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import { Transition } from '@headlessui/react';
import Link from 'next/link';
import classnames from 'classnames';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import useScrollToError from '../lib/utils/hooks/useScrollToError';
import { useRegisterMutation } from '../lib/api/hooks/mutations/Auth/useRegisterMutation';
import { RegisterFields, RegisterPayload } from '../lib/types';
import remoteErrorsToFormErrors from '../lib/utils/remoteErrorsToFormErrors';
import { errorToast } from '../components/Toast/errorToast';
import AnalyticsTracker from '../components/AnalyticsTracker/AnalyticsTracker';
import OpenRoute from '../lib/routes/OpenRoute';
import useDevice from '../lib/utils/hooks/useDevice';
import useReCaptcha from '../lib/utils/hooks/useReCaptcha';
import FormError from '../components/FormError/FormError';
import One from '../components/RegisterScreen/One';
import Two from '../components/RegisterScreen/Two';
import Three from '../components/RegisterScreen/Three';
import Button from '../components/Button/Button';
import { useCheckEmailAvailableMutation } from '../lib/api/hooks/mutations/Auth/useCheckEmailAvailableMutation';

export const termsConfirmationText = 'I agree to the terms and conditions';
export const termsSource = process.env.NEXT_PUBLIC_TERMS_URL || '';

function RegisterScreen() {
	const formMethods = useForm({
		defaultValues: {
			jobTitle: '',
			name: '',
			email: '',
			companyName: '',
			phoneNumber: '',
			companyCountry: 'GB',
			termsConfirmed: false,
			currency: 'GBP',
		},
		shouldUnregister: false,
	});

	const { handleSubmit, errors, setError } = formMethods;
	const [section, setSection] = useState(0);
	const [marketingSection, setMarketingSection] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setMarketingSection(currentSection =>
				currentSection === 3 ? 0 : currentSection + 1,
			);
		}, 5000);
		return () => clearInterval(interval);
	}, []);

	const [loading, setLoading] = useState(false);
	const [registered, setRegistered] = useState(false);
	const [captchaError, setCaptchaError] = useState('');

	useScrollToError(errors);

	const checkEmailMutation = useCheckEmailAvailableMutation({
		onSuccess: () => {
			setSection(1);
		},
		onError: () => {
			setError('email', { message: 'Email address is already in use.' });
		},
	});
	const registerMutation = useRegisterMutation({
		onSuccess: () => {
			setRegistered(true);
		},
		onSettled: () => {
			setLoading(false);
		},
		onError: errors => {
			if (errors?.response?.data === 'CAPTCHA validation failed')
				setCaptchaError('Invalid captcha. Please refresh and try again');
			remoteErrorsToFormErrors<RegisterFields>(errors, setError);
			captchaRef.reset();
		},
	});

	const { captchaRef, ReCaptcha } = useReCaptcha();

	const onSubmit = ({
		jobTitle,
		name,
		email,
		companyName,
		phoneNumber,
		companyCountry,
		termsConfirmed,
		currency,
	}: RegisterFields) => {
		if (section === 0) {
			return checkEmailMutation.mutate(email);
		}
		if (section !== 2) return setSection(section + 1);

		if (!termsConfirmed) {
			return errorToast('Please accept the terms and conditions');
		}

		let captchaToken = captchaRef.getValue();
		if (!captchaToken)
			return setCaptchaError('Please complete the captcha below.');

		setCaptchaError('');
		setLoading(true);

		const [firstName, surname] = name.split(' ');
		const params: RegisterPayload = {
			firstName: firstName.trim(),
			surname: surname.trim(),
			email: email.trim(),
			companyName: companyName.trim(),
			companyEmail: email.trim(),
			phoneNumber,
			companyCountry,
			termsConfirmed,
			termsConfirmationText,
			termsSource,
			jobTitle: jobTitle.trim(),
			currency,
		};

		registerMutation.mutate({ params, captchaToken });
	};

	const { isMobile } = useDevice();

	const renderSection = () => {
		switch (section) {
			case 0:
				return <One />;
			case 1:
				return <Two />;
			case 2:
				return <Three />;
			default:
				return null;
		}
	};

	const renderMarketingSection = () => {
		return (
			<div>
				<Transition
					show={marketingSection === 0}
					enter='transition-opacity duration-150'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leaveTo='opacity-0'
				>
					<h1 className='text-xl font-light italic text-center'>
						Seamlessly upload candidates
					</h1>
					<img
						src='/images/CandidateUploadGraphic.png'
						alt='graphic showing uploading a candidate'
					/>
				</Transition>
				<Transition
					show={marketingSection === 1}
					enter='transition-opacity duration-150'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leaveTo='opacity-0'
				>
					<h1 className='text-xl font-light italic text-center'>
						Manage your hiring flow
					</h1>
					<img
						src='/images/HiringFlowGraphic.png'
						alt='graphic showing your hiring flow'
					/>
				</Transition>
				<Transition
					show={marketingSection === 2}
					enter='transition-opacity duration-150'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leaveTo='opacity-0'
				>
					<h1 className='text-xl font-light italic text-center'>
						Collate feedback on a candidate
					</h1>
					<img
						src='/images/CandidateRatingGraphic.png'
						alt='graphic showing rating a candidate'
					/>
				</Transition>
				<Transition
					show={marketingSection === 3}
					enter='transition-opacity duration-150'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leaveTo='opacity-0'
				>
					<h1 className='text-xl font-light italic text-center'>
						Create your public Careers Page
					</h1>
					<img
						src='/images/JobBoardGraphic.png'
						alt='graphic showing your public job board'
					/>
				</Transition>
			</div>
		);
	};

	return (
		<div className='w-screen h-screen overflow-hidden flex'>
			{loading && <LoadingOverlay />}
			{isMobile === true && (
				<a
					href='https://www.stryve.online/b-corporation'
					target='_blank'
					rel='noreferrer'
				>
					<img
						className='absolute top-2 right-2'
						src='/images/BCorpBlack.png'
						height='150'
						width='70'
						alt='bcorp logo'
					/>
				</a>
			)}
			<div className='overflow-x-hidden flex w-full h-full xl:min-w-[60vw] flex-col flex-1 items-center justify-center p-8 md:p-12'>
				<div className='flex flex-col items-start w-full h-full max-w-md justify-center'>
					<img
						className='w-auto h-10 mb-4'
						src='/images/logo-black.svg'
						alt="Stryve's Logo"
					/>
					{registered ? (
						<div className='flex flex-col text-lg w-full gap-4 px-8'>
							<h1 className='text-3xl mt-4 leading-10 font-light'>
								Welcome to a more human way to hire!
							</h1>
							<p className='font-normal text-xl mt-4'> Welcome to Stryve!</p>
							<p>
								You will shortly receive an email containing a link to configure
								the rest of your account.
							</p>
							<p>You can now close this tab.</p>
						</div>
					) : (
						<form onSubmit={handleSubmit(onSubmit)} className='w-full'>
							<div className='flex flex-col gap-4 md:gap-8 pb-4'>
								<FormProvider {...formMethods}>{renderSection()}</FormProvider>
								{captchaError && <FormError error={captchaError} />}
								{section === 2 && <ReCaptcha />}
								<div
									className={classnames('flex items-center w-full', {
										'justify-between': section > 0,
										'justify-end': section === 0,
									})}
								>
									{section !== 0 && (
										<Button
											icon={faChevronLeft}
											onClick={() => setSection(section - 1)}
											secondary
											ariaLabel='Back'
											id='Back'
										>
											Back
										</Button>
									)}
									<Button
										icon={section === 2 ? faCheck : faChevronRight}
										onClick={handleSubmit(onSubmit)}
										proceed={section === 2}
										ariaLabel={section !== 2 ? 'Next' : "Let's Recruit"}
										id={section !== 2 ? 'Next' : "Let's Recruit"}
									>
										{section !== 2 ? 'Next' : "Let's Recruit"}
									</Button>
								</div>
							</div>
							<p className='font-light text-green-900 text-lg my-2 text-center'>
								14 day free trial. No card required.
							</p>
							<div className='mt-2 flex items-center justify-center w-full mx-auto'>
								<svg
									width='101'
									height='21'
									viewBox='0 0 101 21'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
									className='transition-colors'
								>
									<circle cx='10.5' cy='10.5' r='10' fill='#37C89F' />
									<path
										d='M40.5 11C40.7761 11 41 10.7761 41 10.5C41 10.2239 40.7761 10 40.5 10V11ZM20.5 11H40.5V10H20.5V11Z'
										fill={section > 0 ? '#37C89F' : '#CED2D7'}
									/>
									<circle
										cx='50.5'
										cy='10.5'
										r='10'
										fill={section > 0 ? '#37C89F' : '#CED2D7'}
									/>
									<path
										d='M80.5 11C80.7761 11 81 10.7761 81 10.5C81 10.2239 80.7761 10 80.5 10V11ZM60.5 11H80.5V10H60.5V11Z'
										fill={section > 1 ? '#37C89F' : '#CED2D7'}
									/>
									<circle
										cx='90.5'
										cy='10.5'
										r='10'
										fill={section > 1 ? '#37C89F' : '#CED2D7'}
									/>
								</svg>
							</div>

							<div className='text-slate-900 flex items-center justify-center w-full text-lg font-light text-center mx-auto mt-8 mb-12'>
								<p>Already have an account?</p>
								<Link href='/login'>
									<a>
										<span className='ml-1 font-semibold text-lightBlue-900 underline'>
											Log in
										</span>
									</a>
								</Link>
							</div>
						</form>
					)}
				</div>
			</div>
			{isMobile === false && (
				<div className='lg:block relative flex-1 h-screen bg-pink-900 w-full'>
					<a
						href='https://www.stryve.online/b-corporation'
						target='_blank'
						rel='noreferrer'
					>
						<img
							className='absolute top-2 right-2'
							src='/images/BCorpBlack.png'
							height='150'
							width='70'
							alt='bcorp logo'
						/>
					</a>
					<div className='flex h-full flex-col items-center justify-center'>
						<div className='flex flex-col justify-center max-w-[70%] m-auto'>
							{renderMarketingSection()}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default function Register() {
	return (
		<Fragment>
			<AnalyticsTracker pageReference='REGISTER' />
			<OpenRoute>
				<RegisterScreen />
			</OpenRoute>
		</Fragment>
	);
}
